import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { motion } from "framer-motion";

import { fetchReportDetails, storeStaffworkers } from "../../actions/reports";

import fadeInUp from "../../helpers/Animations";
import { dollarFormat } from "../../helpers/String";
import Loading from "../Loading";

import "../../styles/components/_programme-details.scss";

import partyPopper from "../../assets/images/party-popper.png";

/*
 * Displays a list of staffworkers and their financial totals based on the selected report.
 */

// eslint-disable-next-line react/prop-types
const ReportDetails = ({
  code,
  loading,
  error,
  staffworkers,
  fetchDetails,
}) => {
  useEffect(() => {
    if (code) {
      fetchDetails(code);
    }
  }, [code, fetchDetails]);

  if (!/[a-zA-Z0-9]/g.test(code)) {
    return null;
  }

  if (loading) {
    return <Loading />;
  }

  // This error means that no data was returned from Filemaker, in which case we have nothing to show, so we show nothing. Any error message being displayed to the user would confusing.
  if (error) {
    return null;
  }

  return staffworkers.length > 0 ? (
    <motion.div
      animate={fadeInUp.animate}
      transition={fadeInUp.transition}
      className="programme-details"
    >
      <h2>Details</h2>
      <div
        className={`programme-details__wrapper-desktop ${
          staffworkers.length === 1 && `programme-details__wrapper--singular`
        }`}
      >
        {staffworkers.map((staffworker) => (
          <div
            className="programme-details__staffworker"
            key={staffworker.name}
          >
            <div className="programme-details__field">
              <strong className="label">Staff</strong>
              <span className="value">{staffworker.name}</span>
            </div>
            <div className="programme-details__field">
              <strong className="label">Category</strong>
              <span className="value">{staffworker.category}</span>
            </div>
            <div className="programme-details__wrapper">
              <div className="programme-details__field">
                <strong className="label">Days</strong>
                <span className="value">{staffworker.days}</span>
              </div>
              <div className="programme-details__field dollars">
                <strong className="label">Monthly Target</strong>
                <span className="value">
                  {dollarFormat(staffworker.monthly_target)}
                </span>
              </div>
              <div className="programme-details__field dollars">
                <strong className="label">Yearly Target</strong>
                <span className="value">
                  {dollarFormat(staffworker.yearly_target)}
                </span>
              </div>
            </div>
          </div>
        ))}
        {staffworkers.length > 1 && (
          <div className="programme-details__wrapper programme-details__totals">
            <strong className="programme-details__field">
              Combined Regular Support Target
            </strong>
            <span className="programme-details__field dollars">
              {dollarFormat(staffworkers[0].combined_monthly_target)}
            </span>
            <span className="programme-details__field dollars">
              {dollarFormat(staffworkers[0].combined_yearly_target)}
            </span>
          </div>
        )}
        <div className="programme-details__wrapper programme-details__totals">
          <strong className="programme-details__field">{`${
            staffworkers.length > 1 ? "Combined" : "Your"
          } Regular Support`}</strong>
          <span className="programme-details__field dollars">
            {dollarFormat(staffworkers[0].combined_monthly_actual)}
          </span>
          <span className="programme-details__field dollars">
            {dollarFormat(staffworkers[0].combined_yearly_actual)}
          </span>
        </div>
      </div>
      {staffworkers[0].combined_progress && (
        <div className="programme-details__wrapper programme-details__targets">
          <div className="programme-details__field">
            <strong className="label">Regular Support</strong>
            <div className="progress">
              <div
                style={{ width: `${staffworkers[0].combined_progress}%` }}
                className="progress__value"
              >
                {staffworkers[0].combined_progress.toFixed(2)}%
                {staffworkers[0].combined_progress > 100 && (
                  <img
                    // eslint-disable-next-line no-undef
                    src={partyPopper}
                    alt="party-popper"
                    className="party-popper"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  ) : null;
};

ReportDetails.defaultProps = {
  code: "",
  loading: false,
  error: "",
  details: {},
  staffworkers: [],
};

ReportDetails.propTypes = {
  code: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  details: PropTypes.object,
  staffworkers: PropTypes.arrayOf(PropTypes.object),
  fetchDetails: PropTypes.func.isRequired,
  setStaffworkers: PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  fetchDetails: (code) => dispatch(fetchReportDetails(code)),
  setStaffworkers: (staffworkers) => dispatch(storeStaffworkers(staffworkers)),
});

const mapStateToProps = (state) => ({
  code: state.reports.programme.code,
  name: state.reports.programme.name,
  details: state.reports.details,
  staffworkers: state.reports.staffworkers,
  loading: state.reports.details.loading,
  error: state.reports.details.error,
});

export default connect(mapStateToProps, mapDispatchToProps)(ReportDetails);
